// Der Datenvertrag für den Happy-Energy-Ablauf (E3c – PLAN.md, „E3c“;
// ANFORDERUNGEN.md H27 bis H31, G19, G20): nach dem Freigeben die
// Bestellung beim Lieferanten, die Dachpartie und die Bodenpartie. Seit S1
// stehen hier die Wertelisten der neuen Tabellen (partner, bestellung,
// einsatz – server/src/db/schema.ts), die Lieferanten und Partien der Firma
// und die Vorgaben, die die Leitung dafür einstellt: die zwei Mailtexte mit
// Platzhaltern, der Vorlauf der Lieferung und die Adresse von K2.
//
// Grundregeln:
//  - Lieferanten und Partien werden nie gelöscht, nur „nicht mehr
//    angeboten“ (`aktiv: false`) – Bestellungen und Einsätze verweisen auf
//    sie und bleiben lesbar (Prüfung des Plans E3c, Befund 8). Ein
//    Ansprechpartner lässt sich einzeln entfernen: Er ist ein Eintrag in der
//    Liste, auf ihn verweist nichts.
//  - Ansprechpartner sind Daten Dritter (Name, Mail, Telefon von Leuten beim
//    Lieferanten) – ins Verarbeitungsverzeichnis (O30). Der Verlauf bekommt
//    davon nur Kennungen.
//  - Die Mailtexte: Solange die Firma keinen eigenen Text gespeichert hat,
//    gilt der Vorschlag von hier (`STANDARD_MAILVORLAGEN`) – so steht er an
//    EINER Stelle und wird besser, ohne dass jede Firma nachziehen muss.

import * as z from "zod";
import { kalendertag, type BueroAbweisung, type Montagetermin, type NutzerKurz, type PruefHinweis } from "./buero-vertrag.ts";
import type { DachFakten } from "./dach-fakten.ts";
import type { Kette, SchrittStand } from "./kette.ts";
import { email, freiwillig, kennung, pflicht } from "./vertrag-bausteine.ts";

/** Die Grenzen des Vertrags – an einer Stelle, damit App und Server dieselben kennen. */
export const ABLAUF_GRENZEN = {
  /** Name eines Lieferanten, einer Partie oder eines Ansprechpartners */
  name: 120,
  telefon: 40,
  /** Ansprechpartner je Lieferant oder Partie (Manuel: „pro Firma zwei Ansprechpartner“ – einer Reserve). */
  ansprechpartner: 3,
  /** Betreff einer Mail */
  betreff: 150,
  /**
   * Text einer Mail. Die Mail geht als `mailto:`-Link ans Mailprogramm
   * (Weg A, DECISIONS.md 2.11.); Outlook unter Windows kappt den ganzen Link
   * um 2.000 Zeichen – der Text bleibt deshalb deutlich darunter, damit die
   * Platzhalter Platz haben.
   */
  mailtext: 1500,
  /** Der Vorlauf der Lieferung in Werktagen vor dem Montagetermin */
  vorlaufMax: 20,
  /** Die Adresse von K2 */
  k2Adresse: 500,
  /** Seit S3: die Module – Anzahl (wie die Prüfung der Datenbank, anlage_module_anzahl) und Fabrikat. */
  moduleMax: 10000,
  fabrikat: 120,
  /** So viele bisher verwendete Fabrikate gibt es zum Antippen – die zuletzt verwendeten. */
  fabrikateZumAntippen: 12,
} as const;

// --- Feste Wertelisten (die Datenbank prüft sie mit – server/src/db/schema.ts) -----

/** Lieferant (Unterkonstruktion, Module …), Dachpartie (extern, H31), Bodenpartie (die eigenen Leute). */
export const PARTNER_ARTEN = ["lieferant", "dachpartie", "bodenpartie"] as const;
export type PartnerArt = (typeof PARTNER_ARTEN)[number];

export const PARTNER_ART_NAMEN: Record<PartnerArt, { einer: string; mehrere: string }> = {
  lieferant: { einer: "Lieferant", mehrere: "Lieferanten" },
  dachpartie: { einer: "Dachpartie", mehrere: "Dachpartien" },
  bodenpartie: { einer: "Bodenpartie", mehrere: "Bodenpartien" },
};

/** Was bestellt wird – je Teil ein Lieferant (H27, H30). */
export const BESTELL_TEILE = ["unterkonstruktion", "module", "wechselrichter_speicher"] as const;
export type BestellTeil = (typeof BESTELL_TEILE)[number];

export const BESTELL_TEIL_NAMEN: Record<BestellTeil, string> = {
  unterkonstruktion: "Unterkonstruktion",
  module: "Module",
  wechselrichter_speicher: "Wechselrichter und Speicher",
};

/**
 * Wie eine Bestellung erledigt wurde: per Mail aus dem CRM (Weg A) – oder
 * „schon erledigt“: Manuel hat vor dem Update ohne System bestellt (PLAN.md,
 * E3c, „Jeder Schritt lässt sich auch ‚schon erledigt‘ setzen“). Weg B (das
 * System verschickt selbst) kommt mit dem Vertrag dazu.
 */
export const BESTELL_WEGE = ["mail", "schon-erledigt"] as const;
export type BestellWeg = (typeof BESTELL_WEGE)[number];

/** Ein Einsatz am Dach (Dachpartie) oder am Boden (Bodenpartie). */
export const EINSATZ_ARTEN = ["dach", "boden"] as const;
export type EinsatzArt = (typeof EINSATZ_ARTEN)[number];

/**
 * Wie ein Einsatz beauftragt wurde: die Dachpartie per Mail, die Bodenpartie
 * mit der gedruckten Unterlage (Gernot, 2.11.: „vorerst druckt sie das
 * Büro aus“) – oder „schon erledigt“ wie bei der Bestellung.
 */
export const EINSATZ_WEGE = ["mail", "druck", "schon-erledigt"] as const;
export type EinsatzWeg = (typeof EINSATZ_WEGE)[number];

/**
 * Warum es eine Bestellung oder einen Einsatz gibt (seit S2, Migration 0010,
 * Spalte `anlass`) – leer: der normale Weg, das erste Mal.
 *  - vor-dem-update: beim Update von der Datenbank als „schon erledigt“
 *    übernommen – Anlagen, die vor dem Update freigegeben waren und deren
 *    Montagetermin schon vorbei war (Prüfung des Plans, Befund 14). Ohne
 *    Person; die Kette sagt „vor dem Update erledigt“.
 *  - die übrigen: Nach dem Abhaken hat sich etwas geändert (Befund 7) – die
 *    neue Zeile ersetzt eine erledigte (`vorgaenger_id`), der Schritt ist
 *    „wieder offen“, und der Anlass ist sein Grund („Termin geändert – Mail
 *    erneut öffnen“). Angelegt werden sie von der Bestellung (S3) und den
 *    Partien (S4).
 */
export const ZEILEN_ANLAESSE = [
  "vor-dem-update",
  "termin-geaendert",
  "lieferant-geaendert",
  "module-geaendert",
  "liefertermin-geaendert",
  "partie-geaendert",
  "zeit-geaendert",
] as const;
export type ZeilenAnlass = (typeof ZEILEN_ANLAESSE)[number];

/** Was sich geändert hat – der Anfang des Grundes für „wieder offen“ (kette.ts). */
export const ANLASS_WORTE: Record<Exclude<ZeilenAnlass, "vor-dem-update">, string> = {
  "termin-geaendert": "Termin geändert",
  "lieferant-geaendert": "Lieferant geändert",
  "module-geaendert": "Anzahl oder Fabrikat der Module geändert",
  "liefertermin-geaendert": "Liefertermin geändert",
  "partie-geaendert": "Partie geändert",
  "zeit-geaendert": "Tag oder Uhrzeit geändert",
};

// --- Lieferanten und Partien -------------------------------------------------------------

/** Ein Ansprechpartner: der Name Pflicht, Mail und Telefon freiwillig. */
export const ansprechpartnerEingabe = z.object({
  name: pflicht(ABLAUF_GRENZEN.name, "Bitte einen Namen eingeben."),
  email,
  telefon: freiwillig(ABLAUF_GRENZEN.telefon),
});
export type Ansprechpartner = z.infer<typeof ansprechpartnerEingabe>;

/**
 * Ein Lieferant oder eine Partie, wie die Leitung sie pflegt – anlegen und
 * ändern nach der Kennung (sie entsteht im Browser, wie bei der
 * Standardliste). Die Art bleibt, wie sie angelegt wurde. `aktiv: false`:
 * „nicht mehr anbieten“ – nie löschen.
 */
export const partnerEingabe = z.object({
  id: kennung,
  art: z.enum(PARTNER_ARTEN),
  name: pflicht(ABLAUF_GRENZEN.name, "Bitte einen Namen eingeben."),
  ansprechpartner: z
    .array(ansprechpartnerEingabe)
    .max(ABLAUF_GRENZEN.ansprechpartner, `Höchstens ${ABLAUF_GRENZEN.ansprechpartner} Ansprechpartner.`),
  aktiv: z.boolean(),
});
export type PartnerEingabe = z.infer<typeof partnerEingabe>;

// --- Die Mailtexte ------------------------------------------------------------------------

/** Die zwei Mails, die das CRM fertig ausfüllt: an den Lieferanten und an die Dachpartie. */
export const MAIL_ARTEN = ["bestellung", "dachpartie"] as const;
export type MailArt = (typeof MAIL_ARTEN)[number];

export interface MailVorlage {
  betreff: string;
  text: string;
}

/** Ein Platzhalter im Text – `wort` steht in eckigen Klammern im Text: „[Liefertermin]“. */
export interface Platzhalter {
  wort: string;
  /** Was dafür eingesetzt wird – ein Satz für die Einstellungen. */
  heisst: string;
}

/**
 * Die Platzhalter je Mail. Was an wen geht, steht im Plan (E3c, „Was an die
 * Dachpartie geht“): Lieferanten bekommen Adresse und Mengen, die Dachpartie
 * dazu Termin und den Namen des Kunden – nie seine Telefonnummer, nie
 * Notizen.
 */
export const MAIL_PLATZHALTER: Record<MailArt, readonly Platzhalter[]> = {
  bestellung: [
    { wort: "Ansprechpartner", heisst: "der Name des Ansprechpartners, so wie er beim Lieferanten steht" },
    { wort: "Liefertermin", heisst: "der Wunschliefertermin, z. B. „Mi., 28.10.2026“" },
    { wort: "Adresse", heisst: "die Lieferadresse – die Adresse der Anlage" },
    { wort: "Teile", heisst: "was dieser Lieferant liefert, je Teil eine Zeile – z. B. „26 Module, Trina Vertex S+“" },
    { wort: "Firma", heisst: "der Name deiner Firma" },
  ],
  dachpartie: [
    { wort: "Ansprechpartner", heisst: "der Name des Ansprechpartners, so wie er bei der Dachpartie steht" },
    { wort: "Termin", heisst: "Tag und Uhrzeit der Montage, z. B. „Mo., 9.11.2026, 7:30 Uhr“" },
    { wort: "Kunde", heisst: "der Name des Kunden – er läutet an der Tür" },
    { wort: "Adresse", heisst: "die Adresse der Anlage" },
    { wort: "Module", heisst: "Anzahl und Fabrikat der Module, z. B. „26 Module, Trina Vertex S+“" },
    { wort: "Firma", heisst: "der Name deiner Firma" },
  ],
};

/**
 * Der Vorschlag je Mail – nach Manuels Beispiel (H28: „Hallo Anes, wie
 * besprochen, bitte um Lieferung. Termin …, Adresse …, anbei die
 * Unterkonstruktionsliste … Module …“). Gilt, solange die Firma keinen
 * eigenen Text gespeichert hat.
 */
export const STANDARD_MAILVORLAGEN: Record<MailArt, MailVorlage> = {
  bestellung: {
    betreff: "Lieferung am [Liefertermin] – [Adresse]",
    text: [
      "Hallo [Ansprechpartner],",
      "",
      "wie besprochen bitte um Lieferung:",
      "",
      "[Teile]",
      "",
      "Liefertermin: [Liefertermin]",
      "Lieferadresse: [Adresse]",
      "",
      "Danke und liebe Grüße",
      "[Firma]",
    ].join("\n"),
  },
  dachpartie: {
    betreff: "Montage am [Termin] – [Adresse]",
    text: [
      "Hallo [Ansprechpartner],",
      "",
      "wie besprochen die Montage am Dach:",
      "",
      "Termin: [Termin]",
      "Kunde: [Kunde]",
      "Adresse: [Adresse]",
      "Module: [Module]",
      "",
      "Die Unterlage mit den Dachfotos und der Planung ist im Anhang.",
      "",
      "Danke und liebe Grüße",
      "[Firma]",
    ].join("\n"),
  },
};

/** Ein Wort in eckigen Klammern – höchstens 40 Zeichen, ohne Zeilenumbruch. */
const PLATZHALTER_MUSTER = /\[([^[\]\n]{1,40})\]/g;

/** Die Platzhalter, die im Text stehen, die diese Mail aber nicht kennt – für einen Hinweis in den Einstellungen (Tippfehler). */
export function unbekanntePlatzhalter(text: string, art: MailArt): string[] {
  const bekannt = new Set(MAIL_PLATZHALTER[art].map((p) => p.wort));
  const unbekannt = new Set<string>();
  for (const [, wort] of text.matchAll(PLATZHALTER_MUSTER)) if (wort !== undefined && !bekannt.has(wort)) unbekannt.add(wort);
  return [...unbekannt];
}

/**
 * Setzt die Werte in die Platzhalter ein: „[Liefertermin]“ → „Mi., 28.10.2026“.
 * Ein Platzhalter ohne Wert bleibt stehen, wie er ist – so sieht man in der
 * Vorschau, was fehlt.
 */
export function mailFuellen(text: string, werte: Partial<Record<string, string>>): string {
  return text.replace(PLATZHALTER_MUSTER, (ganz, wort: string) => werte[wort] ?? ganz);
}

/** Der Text, der für diese Mail gilt: der eigene der Firma – oder der Vorschlag. */
export function mailvorlageFuer(eigene: Partial<Record<MailArt, MailVorlage>>, art: MailArt): MailVorlage & { vorschlag: boolean } {
  const e = eigene[art];
  return e ? { ...e, vorschlag: false } : { ...STANDARD_MAILVORLAGEN[art], vorschlag: true };
}

export const mailvorlageEingabe = z.object({
  betreff: pflicht(ABLAUF_GRENZEN.betreff, "Bitte einen Betreff eingeben."),
  text: z
    .string()
    .trim()
    .min(1, "Bitte einen Text eingeben.")
    .max(ABLAUF_GRENZEN.mailtext, `Höchstens ${ABLAUF_GRENZEN.mailtext} Zeichen – die Mail geht sonst nicht ganz ins Mailprogramm.`),
});

// --- Die Vorgaben der Firma für den Ablauf ------------------------------------------------

/** Die Adresse von K2 – nur http(s), sie wird ein Link. */
const k2Adresse = z
  .string()
  .trim()
  .max(ABLAUF_GRENZEN.k2Adresse)
  .pipe(z.url({ protocol: /^https?$/, message: "Bitte eine Adresse, die mit https:// beginnt." }));

/**
 * Die Vorgaben ändern – nur, was mitkommt, ändert sich. Eine Mailvorlage
 * `null`: zurück zum Vorschlag. Die K2-Adresse `null`: keine.
 */
export const ablaufEinstellungenEingabe = z
  .object({
    mailvorlagen: z
      .object({
        bestellung: mailvorlageEingabe.nullable().optional(),
        dachpartie: mailvorlageEingabe.nullable().optional(),
      })
      .optional(),
    lieferVorlaufWerktage: z
      .int("Bitte eine ganze Zahl.")
      .min(0, "Nicht weniger als 0.")
      .max(ABLAUF_GRENZEN.vorlaufMax, `Höchstens ${ABLAUF_GRENZEN.vorlaufMax} Werktage.`)
      .optional(),
    k2Adresse: k2Adresse.nullable().optional(),
  })
  .refine(
    (e) => e.mailvorlagen?.bestellung !== undefined || e.mailvorlagen?.dachpartie !== undefined || e.lieferVorlaufWerktage !== undefined || e.k2Adresse !== undefined,
    "Es fehlt, was geändert werden soll.",
  );
export type AblaufEinstellungenEingabe = z.infer<typeof ablaufEinstellungenEingabe>;

// --- Die Wege und ihre Antworten ---------------------------------------------------------
//
//   GET   /api/crm/partner          PartnerAntwort       (Leitung, Büro – Recht „partner“)
//   POST  /api/crm/partner          BueroAntwort         (partnerEingabe – nur die Leitung, „partner-pflegen“)
//   GET   /api/crm/firma/ablauf     AblaufEinstellungen  (Leitung, Büro – Recht „partner“: das Büro braucht Texte und Vorlauf)
//   POST  /api/crm/firma/ablauf     BueroAntwort         (ablaufEinstellungenEingabe – nur die Leitung, „einstellungen“)
//
// Seit S2 (die Kette, kette.ts):
//
//   GET   /api/crm/zu-tun                        ZuTunAntwort          (Leitung, Büro – Recht „zu-tun“)
//   GET   /api/crm/zaehler                       ZaehlerAntwort        (jede Rolle im CRM – Recht „fragen“; „zu tun“ nur, wer es darf)
//   POST  /api/crm/anlage/:id/schon-erledigt     SchonErledigtAntwort  („alles bis zur Montage schon erledigt“ – Rechte „bestellen“ und „einsatz“)

/** Ein Lieferant oder eine Partie, wie die Seite sie zeigt. */
export interface PartnerZeile {
  id: string;
  art: PartnerArt;
  name: string;
  ansprechpartner: Ansprechpartner[];
  aktiv: boolean;
}

/** Die Antwort auf GET /api/crm/partner: je Art (Lieferanten, Dachpartien, Bodenpartien), darin die aktiven zuerst, dann nach Namen. */
export interface PartnerAntwort {
  partner: PartnerZeile[];
}

/** Die Antwort auf GET /api/crm/firma/ablauf: die Vorgaben, wie sie jetzt gelten – ein Text ohne eigenen ist der Vorschlag. */
export interface AblaufEinstellungen {
  mailvorlagen: Record<MailArt, MailVorlage & { vorschlag: boolean }>;
  /** Die Lieferung so viele Werktage (Mo bis Fr, ohne Feiertage) vor dem Montagetermin – Vorgabe 1 (O100). */
  lieferVorlaufWerktage: number;
  /** Die Adresse von K2 für „Dachplanung starten“ (S5) – null: noch keine. */
  k2Adresse: string | null;
}

// --- Seit S2: „Zu tun“, die Zahlen der Seitenleiste, „schon erledigt“ -------------------------

/**
 * Eine Anlage auf „Zu tun“: wer, wo, wann montiert wird, und ihre Kette –
 * daraus zeigt die Seite die Schritte, die jetzt gehen, als Knöpfe
 * (kette.ts, zuTunSchritte), und in einem Satz, was als Nächstes kommt.
 */
export interface ZuTunKarte {
  anlageId: string;
  /** Die jüngste gesendete Besichtigung – „Freigeben“ führt zu ihr. */
  aufnahmeId: string;
  kundeName: string;
  strasse: string;
  plz: string;
  ort: string;
  montagetermin: Montagetermin;
  /** Wann sie gesendet wurde – „Zu tun“ zeigt die älteste zuerst. */
  gesendetAm: string;
  /** Wie bisher: neu, mit Nachtrag, oder nach der Freigabe kam etwas dazu („bitte ansehen“). */
  hinweis: PruefHinweis | null;
  /** Offene Fragen an den Besichtigungen dieser Anlage. */
  offeneFragen: number;
  kette: Kette;
}

/** Die Antwort auf GET /api/crm/zu-tun – die älteste gesendete zuerst. */
export interface ZuTunAntwort {
  anlagen: ZuTunKarte[];
}

/**
 * Die Antwort auf GET /api/crm/zaehler (O69): die Zahlen der Seitenleiste –
 * klein, damit sie jede Minute gefragt werden kann, ohne die ganzen Listen.
 * `zuTun` null: nicht für diese Rolle. `fragenStand` ändert sich, sobald
 * sich an den offenen Fragen etwas ändert (neu, beantwortet, erledigt) – erst
 * dann holt das CRM die Liste der offenen Fragen neu.
 */
export interface ZaehlerAntwort {
  zuTun: number | null;
  offeneFragen: number;
  fragenStand: string;
}

/** „Alles bis zur Montage schon erledigt“: `geaendert` – ob etwas vermerkt wurde (zweimal gedrückt: beim zweiten Mal nichts). */
export type SchonErledigtAntwort = { ok: true; geaendert: boolean } | BueroAbweisung;

// --- Seit S3: der Wunschliefertermin --------------------------------------------------------

/** Ein Tag „JJJJ-MM-TT“ als UTC-Mitternacht – nur zum Zählen von Tagen, ohne Zeitzone und Sommerzeit. */
function alsUtc(tag: string): Date {
  const [jahr, monat, t] = tag.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(jahr, monat - 1, t));
}

/**
 * Der Vorschlag für den Wunschliefertermin (H34): so viele Werktage vor dem
 * Montagetermin, wie die Firma als Vorlauf eingestellt hat (Vorgabe 1, O100).
 * Werktage sind Montag bis Freitag – Feiertage rechnet das CRM (noch) nicht
 * heraus; das sagt die Einstellung des Vorlaufs. 0: am Tag der Montage.
 */
export function lieferterminVorschlag(montagetermin: string, werktage: number): string {
  const d = alsUtc(montagetermin);
  let rest = Math.max(0, Math.trunc(werktage));
  while (rest > 0) {
    d.setUTCDate(d.getUTCDate() - 1);
    const wochentag = d.getUTCDay();
    if (wochentag !== 0 && wochentag !== 6) rest -= 1;
  }
  return d.toISOString().slice(0, 10);
}

/** Der Vorschlag – ohne Montagetermin keiner (null). */
export function lieferterminFuer(montagetermin: string | null, werktage: number): string | null {
  return montagetermin === null ? null : lieferterminVorschlag(montagetermin, werktage);
}

/**
 * Der Vorlauf in wenigen Wörtern – neben dem vorgeschlagenen Tag in der Karte
 * „Bestellung“ (G19: was das CRM rechnet, steht da). Bis 3.11.2026 stand die
 * ganze Regel als eigene Zeile darunter; Gernot im Augentest: zu viele Zeilen.
 */
export function vorlaufText(werktage: number): string {
  if (werktage <= 0) return "am Tag der Montage";
  return `${werktage === 1 ? "1 Werktag" : `${werktage} Werktage`} vor der Montage`;
}

// --- Seit S3 und S4: die Eingaben der Karten „Bestellung“, „Dachpartie“, „Bodenpartie“ ---------
//
//   POST  /api/crm/anlage/:id/bestellung                 bestellungEingabe            (Recht „bestellen“)
//   POST  /api/crm/anlage/:id/module                     moduleEingabe                („bestellen“)
//   POST  /api/crm/anlage/:id/bestellt                   bestelltEingabe              („bestellen“)
//   POST  /api/crm/anlage/:id/bestellung/schon-erledigt  teilSchonErledigtEingabe     („bestellen“)
//   POST  /api/crm/anlage/:id/einsatz                    einsatzEingabe               („einsatz“)
//   POST  /api/crm/anlage/:id/beauftragt                 beauftragtEingabe            („einsatz“)
//   POST  /api/crm/anlage/:id/einsatz/schon-erledigt     einsatzSchonErledigtEingabe  („einsatz“)
//   GET   /api/crm/bestellungen                          BestellKisteAntwort          (die Kiste Bestellung – „bestellen“)
//   GET   /api/crm/montagen                              MontagenAntwort              (die Kisten Termin und Montage – „einsatz“)
//
// Alle schreibenden antworten mit BueroAntwort. Die Regeln (server/src/crm/
// bestellung.ts, einsatz.ts, termin-folgen.ts): Was noch nicht abgehakt ist,
// ändert sich an Ort und Stelle; ändert sich nach dem Abhaken etwas, das in
// der Mail stand (Lieferant, Liefertermin, Module; Partie, Tag, Uhrzeit; der
// Montagetermin), wird der Schritt wieder offen – eine neue Zeile ersetzt die
// erledigte, mit dem Grund (ZEILEN_ANLAESSE).

/** Eine Uhrzeit „07:30“ – 24 Stunden, mit führender Null. */
export const uhrzeit = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Bitte eine Uhrzeit wie 07:30.");

/**
 * Bei wem ein Teil bestellt wird und wann geliefert werden soll. Ohne
 * Wunschliefertermin (null): der Vorschlag aus dem Montagetermin. Lieferant
 * null: Wechselrichter und Speicher kommen doch nicht dazu – nur bei diesem
 * Teil und nur, solange er nicht bestellt ist.
 */
export const bestellungEingabe = z.object({
  teil: z.enum(BESTELL_TEILE),
  lieferantId: kennung.nullable(),
  wunschliefertermin: kalendertag.nullable(),
});
export type BestellungEingabe = z.infer<typeof bestellungEingabe>;

/** Anzahl und Fabrikat der Module (G17) – beides freiwillig; zum Bestellen der Module braucht es beides. */
export const moduleEingabe = z.object({
  anzahl: z
    .int("Bitte eine ganze Zahl.")
    .min(1, "Mindestens 1 Modul.")
    .max(ABLAUF_GRENZEN.moduleMax, `Höchstens ${ABLAUF_GRENZEN.moduleMax} Module.`)
    .nullable(),
  fabrikat: freiwillig(ABLAUF_GRENZEN.fabrikat),
});
export type ModuleEingabe = z.infer<typeof moduleEingabe>;

/**
 * „Bestellt“ nach dem Abschicken der Mail: alle Teile dieses Lieferanten –
 * genau die Bestellungen, die die Karte gezeigt hat (hat sich inzwischen
 * etwas geändert, 409 „konflikt“, und die Karte lädt neu).
 */
export const bestelltEingabe = z.object({
  lieferantId: kennung,
  bestellungen: z.array(kennung).min(1).max(BESTELL_TEILE.length),
});
export type BestelltEingabe = z.infer<typeof bestelltEingabe>;

/** Ein Teil „schon erledigt“ – vor dem Update ohne System bestellt. */
export const teilSchonErledigtEingabe = z.object({ teil: z.enum(BESTELL_TEILE) });

/** Die Partie, der Tag (Vorschlag: der Montagetermin) und – vor allem bei der Dachpartie – die Uhrzeit. */
export const einsatzEingabe = z.object({
  art: z.enum(EINSATZ_ARTEN),
  partnerId: kennung,
  tag: kalendertag.nullable(),
  uhrzeit: uhrzeit.nullable(),
});
export type EinsatzEingabe = z.infer<typeof einsatzEingabe>;

/** „Beauftragt“ (Dachpartie, nach der Mail) oder „Eingeteilt“ (Bodenpartie, nach dem Drucken) – genau der Einsatz, den die Karte gezeigt hat. */
export const beauftragtEingabe = z.object({ art: z.enum(EINSATZ_ARTEN), einsatzId: kennung });

/** Eine Partie „schon erledigt“ – ohne System beauftragt oder eingeteilt. */
export const einsatzSchonErledigtEingabe = z.object({ art: z.enum(EINSATZ_ARTEN) });

/** Wie ein Einsatz auf dem normalen Weg erledigt wird: die Dachpartie per Mail, die Bodenpartie mit der gedruckten Unterlage. */
export const EINSATZ_WEG_JE_ART: Record<EinsatzArt, Exclude<EinsatzWeg, "schon-erledigt">> = { dach: "mail", boden: "druck" };

/** Welche Art Partner zu welchem Einsatz gehört. */
export const PARTNER_JE_EINSATZ: Record<EinsatzArt, PartnerArt> = { dach: "dachpartie", boden: "bodenpartie" };

// --- Was die Akte dafür liest -------------------------------------------------------------------

/** Eine geltende Bestellung (`ersetzt_am` leer), wie die Karte „Bestellung“ sie zeigt. */
export interface AblaufBestellung {
  id: string;
  teil: BestellTeil;
  lieferantId: string | null;
  wunschliefertermin: string | null;
  weg: BestellWeg;
  /** Bestellt oder „schon erledigt“ – wann; null: noch offen. */
  erledigtAm: string | null;
  erledigtVon: NutzerKurz | null;
  /** Warum es die Zeile gibt – bei „wieder offen“ der Grund (ZEILEN_ANLAESSE). */
  anlass: ZeilenAnlass | null;
  /** War die ersetzte Vorgängerin erledigt? Dann ist diese offen „wieder offen“. */
  vorgaengerErledigt: boolean;
}

/** Ein geltender Einsatz einer Partie. */
export interface AblaufEinsatz {
  id: string;
  art: EinsatzArt;
  partnerId: string | null;
  tag: string | null;
  /** „07:30“ – null: keine. */
  uhrzeit: string | null;
  weg: EinsatzWeg;
  beauftragtAm: string | null;
  beauftragtVon: NutzerKurz | null;
  anlass: ZeilenAnlass | null;
  vorgaengerErledigt: boolean;
}

/** Bestellung und Partien in der Akte (seit S3, S4) – was die drei Karten brauchen. */
export interface AkteAblauf {
  bestellungen: AblaufBestellung[];
  einsaetze: AblaufEinsatz[];
  /** Anzahl und Fabrikat der Module (G17) – an der Anlage. */
  module: { anzahl: number | null; fabrikat: string | null };
  /** Die Fabrikate, die die Firma bisher verwendet hat – zum Antippen, die zuletzt verwendeten zuerst. */
  fabrikate: string[];
  /** Der Excel-Report von K2 (die Unterkonstruktionsliste) – Prüfsumme; null: noch keiner. */
  k2Excel: string | null;
  /** Die Gegebenheiten am Dach aus der Besichtigung (dach-fakten.ts) – null: nicht erfasst. */
  dach: DachFakten | null;
  /** Die jüngste freigegebene Besichtigung – ihre Unterlage bekommt die Bodenpartie, ihre Dachfotos die Dachpartie. null: noch keine. */
  unterlageAufnahmeId: string | null;
}

// --- Die Kisten Bestellung, Termin und Montage ---------------------------------------------------

/** Ein Teil in der Kiste Bestellung – bei wem, wann geliefert, ob bestellt. */
export interface BestellKisteTeil {
  teil: BestellTeil;
  /** Der Name des Lieferanten – null: noch keiner gewählt (oder „schon erledigt“ ohne). */
  lieferant: string | null;
  wunschliefertermin: string | null;
  erledigtAm: string | null;
}

/** Eine Anlage in der Kiste Bestellung. */
export interface BestellKisteZeile {
  anlageId: string;
  kundeName: string;
  strasse: string;
  plz: string;
  ort: string;
  montagetermin: Montagetermin;
  /** Der Stand des Schritts „Bestellt“ aus der Kette – mit seinem Grund („noch offen: Module“). */
  stand: SchrittStand;
  grund: string | null;
  /** Die Teile, die dazugehören: Unterkonstruktion und Module immer, Wechselrichter und Speicher, wenn dafür bestellt wird. */
  teile: BestellKisteTeil[];
  /** Wann zuletzt etwas bestellt wurde – null: noch nichts. */
  zuletztBestelltAm: string | null;
}

/** Die Antwort auf GET /api/crm/bestellungen: was offen ist (der nächste Montagetermin zuerst), dann was zuletzt bestellt wurde. */
export interface BestellKisteAntwort {
  offen: BestellKisteZeile[];
  bestellt: BestellKisteZeile[];
}

/** Eine Partie an einer Anlage – für die Kisten Termin und Montage. */
export interface MontagePartie {
  /** Der Name der Partie – null: noch keine gewählt. */
  name: string | null;
  tag: string | null;
  uhrzeit: string | null;
  /** Der Stand des Schritts (Dachpartie beauftragt, Bodenpartie eingeteilt) aus der Kette – mit Grund. */
  stand: SchrittStand;
  grund: string | null;
}

/** Eine Anlage mit Montagetermin – oder freigegeben ohne. */
export interface MontageZeile {
  anlageId: string;
  kundeName: string;
  strasse: string;
  plz: string;
  ort: string;
  montagetermin: Montagetermin;
  dach: MontagePartie;
  boden: MontagePartie;
  /** Die Unterlage für die Montage (jüngste freigegebene Besichtigung) – null: noch nicht freigegeben. */
  unterlageAufnahmeId: string | null;
}

/**
 * Die Antwort auf GET /api/crm/montagen: alle Anlagen, deren Montagetermin
 * oder deren Partie ab heute kommt – nach dem Montagetermin, der nächste
 * zuerst; dazu die freigegebenen ohne Tag als Montagetermin. Montierte nicht.
 * `heute`: der Tag in Wien, ab dem gezählt wurde.
 */
export interface MontagenAntwort {
  heute: string;
  kommend: MontageZeile[];
  ohneTermin: MontageZeile[];
}

// --- Seit S5: die Kiste Dachplanung ---------------------------------------------------------------
//
//   GET   /api/crm/dachplanungen                         DachplanungKisteAntwort      (die Kiste Dachplanung – „kunden“)

/** Eine Anlage in der Kiste Dachplanung – gesendet, noch nicht montiert, und der K2-Plan oder die Liste fehlt. */
export interface DachplanungKisteZeile {
  anlageId: string;
  kundeName: string;
  strasse: string;
  plz: string;
  ort: string;
  /** Wann die Besichtigung gesendet wurde – danach ist geordnet, die älteste zuerst. */
  gesendetAm: string;
  montagetermin: Montagetermin;
  /** Was von K2 bei der Anlage liegt: der Plan (PDF), die Liste (Excel-Report), „übersprungen“ draußen. */
  k2: { pdf: boolean; excel: boolean; uebersprungen: boolean };
}

/**
 * Die Antwort auf GET /api/crm/dachplanungen (E3c, S5 – an Stelle des
 * Platzhalters): zuerst die Anlagen ohne K2-Plan, dann die mit Plan, aber
 * ohne Excel-Report – je die älteste Besichtigung zuerst. Montierte nicht.
 */
export interface DachplanungKisteAntwort {
  ohnePlan: DachplanungKisteZeile[];
  ohneListe: DachplanungKisteZeile[];
}
